import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import dayjs from "dayjs";
import { SubpageHeader } from "@/components/birthlife/SubpageHeader";
import { Footer } from "@/components/birthlife/Footer";
import { Breadcrumbs } from "@/components/birthlife/Breadcrumbs";
import { Seo, breadcrumbLd, blogLd } from "@/lib/seo";
import { useSite } from "@/lib/site";

const CRUMBS = [{ label: "Blog", href: "/blog/" }];

export default function Blog() {
  const { content } = useSite();
  const posts = (content.posts || []).filter((p) => p.published !== false);

  return (
    <div data-testid="blog-page" className="min-h-screen bg-sand">
      <Seo
        title="The Birth Life Journal — Breastfeeding & Birth Articles"
        description="Evidence-based articles on breastfeeding, birth preparation and infant feeding by Yogyata Wadhwa, IBCLC."
        path="/blog/"
        jsonLd={[blogLd(posts), breadcrumbLd(CRUMBS)]}
      />
      <SubpageHeader />
      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:px-16 lg:py-16">
        <Breadcrumbs items={CRUMBS} />
        <h1 className="mt-8 font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl" data-testid="blog-title">
          The Birth Life <span className="italic text-terra">Journal</span>
        </h1>
        <p className="mt-3 max-w-xl text-base text-plumsoft">
          Evidence-based guidance on breastfeeding, birth preparation and infant feeding.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-6 pb-16 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((p, i) => (
            <Link
              key={p.id || p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-list-card-${i}`}
              className="group flex h-full flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-deep"
            >
              {p.cover_image ? (
                <img src={p.cover_image} alt={p.title} className="aspect-video w-full object-cover" loading="lazy" />
              ) : (
                <div className="flex aspect-video w-full items-center justify-center bg-plum">
                  <img src="/assets/mother-baby-art.png" alt="" className="h-24 w-24 opacity-40 invert" />
                </div>
              )}
              <div className="flex flex-1 flex-col p-6">
                {p.published_at && (
                  <p className="flex items-center gap-1.5 text-[11px] font-outfit uppercase tracking-widest text-faint">
                    <CalendarDays className="h-3 w-3" /> {dayjs(p.published_at).format("D MMM YYYY")}
                  </p>
                )}
                <h2 className="mt-2 font-display text-xl font-semibold leading-snug text-ink transition-colors group-hover:text-terra">
                  {p.title}
                </h2>
                {p.excerpt && <p className="mt-2 flex-1 text-sm leading-relaxed text-plumsoft line-clamp-3">{p.excerpt}</p>}
              </div>
            </Link>
          ))}
          {posts.length === 0 && (
            <p className="col-span-full py-16 text-center text-sm text-faint" data-testid="blog-empty">
              Articles are on their way — check back soon.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
